import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { GeneralMeetingModel } from '../../../models/general-meeting.model';
import { OptionalType } from '../../../models/types/optional.type';
import { GeneralMeetingsResource } from '../../../services/resources/general-meetings.resource';
import { MetaResource } from '../../../services/resources/meta.resource';

@Component({
    selector: 'sl-general-meeting-settings',
    templateUrl: './general-meeting-settings.dialog.html',
    styleUrls: ['./general-meeting-settings.dialog.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GeneralMeetingSettingsDialog implements OnInit {
    isLoading = true;

    formGroup = new FormGroup({
        number: new FormControl<OptionalType<number>>(undefined, Validators.required),
        isOrdinary: new FormControl(false),
        title: new FormControl('', Validators.required),
        place: new FormControl('', Validators.required),
        companyName: new FormControl('', Validators.required),
        startDateTime: new FormControl<OptionalType<Date>>(undefined, Validators.required),
        doorOpening: new FormControl<OptionalType<Date>>(undefined, Validators.required),
        admissionCardsSendingDate: new FormControl<OptionalType<Date>>(undefined, Validators.required),
        inviteNewByDate: new FormControl<OptionalType<Date>>(undefined, Validators.required),
        transactionsByDate: new FormControl<OptionalType<Date>>(undefined, Validators.required),
        webInstructionsToDate: new FormControl<OptionalType<Date>>(undefined, Validators.required),
        numberOfCoupons: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(48)]),
        maxVotesPerShareholder: new FormControl<OptionalType<number>>(undefined, Validators.min(0)),
        maxCapitalPerShareholder: new FormControl<OptionalType<number>>(undefined, Validators.min(0)),
        totalSharesWithVotingRights: new FormControl(0, [Validators.required, Validators.min(0)]),
        gmStatusId: new FormControl<OptionalType<number>>(undefined, Validators.required),
        dmiVote: new FormControl(false),
        splitInstructions: new FormControl(false),
        complexInstructions: new FormControl(false)
    });

    readonly metaResource = inject(MetaResource);

    private generalMeeting?: GeneralMeetingModel;

    private readonly generalMeetingsResource = inject(GeneralMeetingsResource);
    private readonly dialogRef = inject(MatDialogRef);

    ngOnInit(): void {
        this.generalMeetingsResource.getCurrent().subscribe(response => {
            if (!response.value) {
                this.dialogRef.close();
                return;
            }
            this.generalMeeting = response.value;
            this.formGroup.patchValue(response.value);
            this.isLoading = false;
        });
    }

    save(): void {
        this.formGroup.updateValueAndValidity();
        this.formGroup.markAllAsTouched();
        if (this.formGroup.invalid || !this.generalMeeting) {
            return;
        }

        this.isLoading = true;
        this.generalMeetingsResource
            .update(this.generalMeeting.id, {
                ...this.generalMeeting,
                ...(this.formGroup.value as GeneralMeetingModel)
            })
            .subscribe({
                next: response => {
                    if (response.value.isSuccessful) {
                        this.dialogRef.close(true);
                    }
                },
                complete: () => (this.isLoading = false)
            });
    }
}
